/* Certificate type and expiry helpers for pages/Certificates (design eval F28). */

/* ─── Types ─────────────────────────────────────────── */
export interface Certificate {
    certificate_id: string;
    certificate: string;
    private_key?: string;
    type: 'ssh' | 'tls';
    expires: string;
    fingerprint: string;
    // Revocation state, if the list endpoint reports it (any of these).
    revoked?: boolean;
    revoked_at?: string | null;
    status?: string;
    metadata?: {
        subject?: string;
        principals?: string[];
        issued_by?: string;
        issued_at?: string;
    };
}

export const isRevoked = (c: Certificate) => c.revoked === true || !!c.revoked_at || c.status === 'revoked';

/** A live certificate this close to expiry is flagged (design eval F28). */
export const EXPIRING_SOON_DAYS = 14;
const DAY_MS = 86_400_000;

const expiryTime = (c: Pick<Certificate, 'expires'>) => {
    const t = new Date(c.expires).getTime();
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/** Not revoked, not yet expired, and expiring within EXPIRING_SOON_DAYS. */
export function isExpiringSoon(c: Certificate, now = Date.now()): boolean {
    if (isRevoked(c)) return false;
    const t = expiryTime(c);
    return t > now && t - now <= EXPIRING_SOON_DAYS * DAY_MS;
}

export type ExpirySort = 'default' | 'soonest' | 'latest';

/** The list in the chosen expiry order; an unparseable date sorts last either way. */
export function sortByExpiry<T extends Pick<Certificate, 'expires'>>(list: T[], order: ExpirySort): T[] {
    if (order === 'default') return list;
    return [...list].sort((a, b) => {
        const ta = expiryTime(a);
        const tb = expiryTime(b);
        if (ta === tb) return 0;
        if (!Number.isFinite(ta)) return 1;
        if (!Number.isFinite(tb)) return -1;
        return order === 'soonest' ? ta - tb : tb - ta;
    });
}
